import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import { ChevronRight as ChevronRightIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

const BADGE_COLORS = ['#2E4156', '#5A7A95', '#4A6B83', '#3F5768', '#7D96AF'];

const formatLeagueDate = (dateStr) => (dateStr
  ? new Date(dateStr).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
  : null);

const getLeagueKey = (league) => league.id ?? league.leagueId ?? league.slug;

const LeagueRow = ({ league, index, muted = false }) => {
  const dateRange = [formatLeagueDate(league.startDate), formatLeagueDate(league.endDate)].filter(Boolean).join(' – ');
  const teamCount = league.teamCount ? `${league.teamCount} team${league.teamCount === 1 ? '' : 's'}` : null;

  return (
    <Box
      component={league.slug ? RouterLink : 'div'}
      to={league.slug ? `/leagues/${league.slug}/standings` : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.25,
        color: 'inherit',
        textDecoration: 'none',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Avatar
        variant="rounded"
        sx={{
          width: 36,
          height: 36,
          fontSize: '0.9rem',
          fontWeight: 700,
          bgcolor: muted ? 'grey.400' : BADGE_COLORS[index % BADGE_COLORS.length],
        }}
      >
        {(league.name || '?').charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main', wordBreak: 'break-word' }}>
          {league.name}
        </Typography>
        {teamCount && <Typography variant="caption" color="text.secondary" component="p">{teamCount}</Typography>}
        {dateRange && <Typography variant="caption" color="text.secondary" component="p">{dateRange}</Typography>}
      </Box>
      <ChevronRightIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
    </Box>
  );
};

const LeagueRows = ({ leagues, muted }) => leagues.map((league, index) => (
  <React.Fragment key={getLeagueKey(league)}>
    {index > 0 && <Divider />}
    <LeagueRow league={league} index={index} muted={muted} />
  </React.Fragment>
));

const cardSx = { bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1, overflow: 'hidden' };

const CollapsedLeagues = ({ title, leagues }) => {
  if (!leagues.length) return null;
  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{ ...cardSx, mt: 2, padding: '0 !important', '&:before': { display: 'none' }, '&.Mui-expanded': { mt: 2 } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
          {title} ({leagues.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Divider />
        <LeagueRows leagues={leagues} muted />
      </AccordionDetails>
    </Accordion>
  );
};

/** Slim league list for the home sidebar; other and past leagues are collapsed by default. */
const HomeLeagueList = ({ myLeagues = [], otherLeagues = [], pastLeagues = [], loading = false, error = null }) => (
  <Box>
    {(loading || error || myLeagues.length > 0) && (
      <>
        <Typography variant="h6" component="h2" className="section-title">
          My leagues
        </Typography>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box sx={cardSx}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
            ) : (
              <LeagueRows leagues={myLeagues} />
            )}
          </Box>
        )}
      </>
    )}
    <CollapsedLeagues title="Other leagues" leagues={otherLeagues} />
    <CollapsedLeagues title="Past or inactive leagues" leagues={pastLeagues} />
  </Box>
);

export default HomeLeagueList;
